import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useGetCompetitorPropertyHistories } from '~/hooks/apis/price-parity/useGetCompetitorPropertyHistories';

import type { CompetitorProperty } from '~/types/services/priceParrot';

import CompetitorOfferTimelineTrack from './CompetitorOfferTimelineTrack';
import { DATE_FORMAT, TIMELINE_ZOOM_OPTIONS, TimelineZoom } from './constants';
import { SupplierTimeline, fullRange, percentOf, realSaleDate, shiftCenter, ticks, zoomedRange } from './timeline';
import { formatDate, supplierKey, supplierLabel } from './utils';

const LABEL_WIDTH = 180;
const CONTROL_SX = { textTransform: 'none', fontSize: 12 };

interface Props {
  suppliers: Array<CompetitorProperty>;
  selectedCollectionId?: string;
  onSelectCollection?: (collectionId?: string) => void;
}

interface LegendItemProps {
  label: string;
  marker: React.ReactNode;
}

function LegendItem({ label, marker }: LegendItemProps) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {marker}
      <Typography variant="caption">{label}</Typography>
    </Stack>
  );
}

// One row per competitor listing of the hotel, across every collection.
export default function CompetitorOfferTimeline({ suppliers, selectedCollectionId, onSelectCollection }: Props) {
  const now = useMemo(() => dayjs().valueOf(), []);
  const [zoom, setZoom] = useState(TimelineZoom.ALL);
  const [center, setCenter] = useState(now);
  const historyQueries = useGetCompetitorPropertyHistories(suppliers);

  if (historyQueries.some((query) => query.isLoading)) {
    return <CircularProgress size={24} />;
  }

  const errorAlerts = suppliers.flatMap((property, index) => {
    const error = historyQueries[index]?.error;
    return error
      ? [
          <Alert key={supplierKey(property)} severity="error">
            Failed to load {supplierLabel(property, suppliers)} collection history: {error.message}
          </Alert>,
        ]
      : [];
  });
  const timelines: Array<SupplierTimeline> = suppliers.flatMap((property, index) =>
    historyQueries[index]?.error ? [] : [{ property, history: historyQueries[index]?.data ?? [] }],
  );

  if (errorAlerts.length && !timelines.length) {
    return <Stack spacing={1}>{errorAlerts}</Stack>;
  }

  const range = zoom === TimelineZoom.ALL ? fullRange(timelines, now) : zoomedRange(zoom, center);
  const tickValues = ticks(zoom, range);
  const saleEnds = timelines
    .map(({ property }) => realSaleDate(property.saleEnd, now))
    .filter((time): time is number => time !== undefined);
  const hasBookableNights = timelines.some(({ property }) => property.firstAvailableNight);

  const focus = (time: number, nextZoom: TimelineZoom) => {
    setCenter(time);
    setZoom(nextZoom);
  };

  return (
    <Stack spacing={1.5}>
      {errorAlerts}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography variant="body2" color="text.secondary">
          Showing {formatDate(dayjs(range.from).toISOString())} – {formatDate(dayjs(range.to - 1).toISOString())}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={zoom}
            onChange={(_, value: TimelineZoom | null) => value && setZoom(value)}
            sx={{ borderRadius: 2, overflow: 'hidden' }}
          >
            {TIMELINE_ZOOM_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={CONTROL_SX}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton
            size="small"
            aria-label="Previous period"
            disabled={zoom === TimelineZoom.ALL}
            onClick={() => setCenter(shiftCenter(zoom, center, -1))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Button size="small" sx={CONTROL_SX} onClick={() => focus(now, TimelineZoom.MONTH)}>
            Today
          </Button>
          {saleEnds.length > 0 && (
            <Button size="small" sx={CONTROL_SX} onClick={() => focus(Math.min(...saleEnds), TimelineZoom.WEEK)}>
              Sale end date
            </Button>
          )}
          <IconButton
            size="small"
            aria-label="Next period"
            disabled={zoom === TimelineZoom.ALL}
            onClick={() => setCenter(shiftCenter(zoom, center, 1))}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflowX: 'auto', borderRadius: 2 }}>
        <Box minWidth={LABEL_WIDTH + 720}>
          <Stack direction="row" borderBottom={1} borderColor="divider">
            <Box width={LABEL_WIDTH} flexShrink={0} px={2} py={1}>
              <Typography variant="caption" fontWeight="medium">
                Competitor
              </Typography>
            </Box>
            <Box position="relative" flex={1} height={32} borderLeft={1} borderColor="divider">
              {tickValues.map((tick) => (
                <Typography
                  key={tick}
                  variant="caption"
                  color="text.secondary"
                  position="absolute"
                  top={6}
                  whiteSpace="nowrap"
                  sx={{ left: `${percentOf(tick, range)}%`, pl: 0.5 }}
                >
                  {dayjs(tick).format(DATE_FORMAT)}
                </Typography>
              ))}
            </Box>
          </Stack>
          {timelines.map((timeline, index) => (
            <Stack
              key={supplierKey(timeline.property)}
              direction="row"
              alignItems="center"
              borderTop={index ? 1 : 0}
              borderColor="divider"
            >
              <Box width={LABEL_WIDTH} flexShrink={0} px={2}>
                <Typography variant="body2" fontWeight="medium">
                  {supplierLabel(timeline.property, suppliers)}
                </Typography>
                {realSaleDate(timeline.property.saleEnd, now) === undefined && (
                  <Typography variant="caption" color="text.secondary">
                    No sale end date published
                  </Typography>
                )}
              </Box>
              <Box flex={1} borderLeft={1} borderColor="divider">
                <CompetitorOfferTimelineTrack
                  supplier={timeline}
                  range={range}
                  tickValues={tickValues}
                  now={now}
                  selectedCollectionId={selectedCollectionId}
                  onSelectCollection={onSelectCollection}
                />
              </Box>
            </Stack>
          ))}
        </Box>
      </Paper>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <LegendItem label="Today" marker={<Box width={2} height={14} bgcolor="error.main" />} />
        <LegendItem
          label="Sale end date (as published)"
          marker={<Box width={2} height={14} bgcolor="primary.main" />}
        />
        <LegendItem
          label="Collection found the deal"
          marker={<Box width={10} height={10} borderRadius="50%" bgcolor="success.main" />}
        />
        {onSelectCollection && (
          <LegendItem
            label="Collection on show (click a dot to show another)"
            marker={
              <Box
                width={14}
                height={14}
                borderRadius="50%"
                bgcolor="success.main"
                color="success.contrastText"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CheckIcon sx={{ fontSize: 10 }} />
              </Box>
            }
          />
        )}
        <LegendItem
          label="Collection did not find it"
          marker={<Box width={10} height={10} borderRadius="50%" border={2} borderColor="text.disabled" />}
        />
        {hasBookableNights && (
          <LegendItem
            label="Bookable nights"
            marker={<Box width={18} height={10} borderRadius={1} bgcolor="action.selected" />}
          />
        )}
      </Stack>
    </Stack>
  );
}
